import React, { useEffect, useState } from 'react';
import { Mail, MailWarning, MailX, Send, Eye, EyeOff } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { apiService, formatApiError } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { fmtDate } from './AdminModeration';

const EMAIL_LABEL = {
  sent: ['Email sent', 'text-green-700 bg-green-50', Mail],
  failed: ['Email failed', 'text-red-700 bg-red-50', MailX],
  skipped: ['Email skipped', 'text-amber-700 bg-amber-50', MailWarning],
};
const REASON = { member_disabled_email: 'member turned email notifications off', no_email: 'no email on file', provider_error: 'provider error' };

export const EmailStatus = ({ email }) => {
  const [label, cls, Icon] = EMAIL_LABEL[email?.status] || ['Unknown', 'text-gray-600 bg-gray-50', MailWarning];
  return <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full ${cls}`} title={email?.error || ''} data-testid="msg-email-status"><Icon className="w-3 h-3 mr-1" />{label}{email?.reason && email.status !== 'sent' ? ` — ${REASON[email.reason] || email.reason}` : ''}</span>;
};

const MessagesTab = ({ s, reload }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ subject: '', body: '', force_email: false });
  const [busy, setBusy] = useState(false);
  const m = s.member;
  const emailOff = !m.email_notifications;
  const deleted = m.status === 'deleted';

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await apiService.admin.sendMemberMessage(m.id, form);
      const st = r.data.data.email?.status;
      toast({ title: 'Message delivered in-app', description: st === 'sent' ? 'Email sent as well.' : st === 'failed' ? 'Email delivery FAILED — see status below, you can retry.' : 'Email skipped (member preference).', variant: st === 'failed' ? 'destructive' : undefined });
      setForm({ subject: '', body: '', force_email: false }); reload();
    } catch (err) { toast({ title: 'Could not send', description: formatApiError(err), variant: 'destructive' }); } finally { setBusy(false); }
  };
  const resend = async (id) => {
    try { const r = await apiService.admin.resendMemberMessageEmail(m.id, id); toast({ title: r.data.data.email.status === 'sent' ? 'Email sent' : 'Email still failing', description: r.data.data.email.error || '' , variant: r.data.data.email.status === 'sent' ? undefined : 'destructive' }); reload(); }
    catch (err) { toast({ title: 'Resend failed', description: formatApiError(err), variant: 'destructive' }); }
  };

  return (
    <div className="space-y-4" data-testid="member-tab-messages">
      <Card className="p-4">
        <h4 className="font-semibold mb-1">Send a private message</h4>
        <p className="text-xs text-gray-500 mb-3">Delivered as an in-app notification and by email from noreply@flamelink.site (reply-to support). Write only what the member should see — never include passwords, codes, card or billing details.</p>
        {deleted ? <p className="text-sm text-red-600" data-testid="msg-deleted-notice">This account was deleted; messages cannot be delivered.</p> : (
          <form onSubmit={send} className="space-y-3" data-testid="admin-message-form">
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} maxLength={120} placeholder="Subject" required data-testid="admin-message-subject" />
            <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} maxLength={3000} placeholder="Message to the member (min 10 characters)" required data-testid="admin-message-body" />
            {emailOff && <label className="flex items-start gap-2 text-sm"><Checkbox checked={form.force_email} onCheckedChange={(v) => setForm({ ...form, force_email: !!v })} data-testid="admin-message-force-email" /><span>This member turned email notifications off. Email anyway (account or safety matters only).</span></label>}
            <Button type="submit" size="sm" disabled={busy || form.subject.trim().length < 3 || form.body.trim().length < 10} data-testid="admin-message-send-btn"><Send className="w-4 h-4 mr-1" />{busy ? 'Sending…' : 'Send message'}</Button>
          </form>
        )}
      </Card>
      <div className="space-y-2" data-testid="admin-message-history">
        <h4 className="font-semibold text-sm">Message history ({s.messages.length})</h4>
        {s.messages.length === 0 && <p className="text-sm text-gray-500" data-testid="messages-empty">No messages sent to this member yet.</p>}
        {s.messages.map((x) => <ThreadCard key={x.id} x={x} member={m} deleted={deleted} onResend={resend} reload={reload} />)}
      </div>
    </div>
  );
};

const ThreadCard = ({ x, member, deleted, onResend, reload }) => {
  const { toast } = useToast();
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const closed = x.status === 'closed';
  const replies = x.replies || [];
  const act = async (fn, okTitle) => {
    setBusy(true);
    try { const r = await fn(); toast({ title: okTitle(r) }); reload(); } catch (e) { toast({ title: 'Action failed', description: formatApiError(e), variant: 'destructive' }); } finally { setBusy(false); }
  };
  useEffect(() => { if (x.member_unread_for_admin) apiService.admin.markMemberThreadSeen(member.id, x.id).catch(() => {}); }, [x.id]);
  return (
    <Card className={`p-3 text-sm ${x.member_unread_for_admin ? 'border-rose-300' : ''}`} data-testid={`admin-message-${x.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{x.subject}</span>
        <span className="text-xs text-gray-500">from {x.admin_username} · {fmtDate(x.created_at)}</span>
        {closed && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700" data-testid="thread-closed-badge">Closed</span>}
        {x.member_unread_for_admin && <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-700" data-testid="thread-member-replied-badge">Member replied</span>}
        <span className="ml-auto flex items-center gap-2">
          <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full ${x.read_in_app ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-testid="msg-read-status">{x.read_in_app ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}{x.read_in_app ? 'Read in app' : 'Unread in app'}</span>
          <EmailStatus email={x.email} />
          {x.email?.status !== 'sent' && !deleted && <Button size="sm" variant="outline" onClick={() => onResend(x.id)} data-testid={`msg-resend-${x.id}`}>Resend email</Button>}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-gray-700 mt-2">{x.body}</p>
      {replies.length > 0 && (
        <ul className="mt-3 space-y-2 border-l-2 border-gray-200 pl-3" data-testid={`thread-replies-${x.id}`}>
          {replies.map((r) => (
            <li key={r.id} className={`rounded-md p-2 ${r.author === 'member' ? 'bg-rose-50' : 'bg-gray-50'}`} data-testid={`thread-reply-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500"><span className="font-medium text-gray-800">{r.author === 'member' ? `${r.author_name || 'Member'} (member)` : `${r.author_name} (admin)`}</span><span>{fmtDate(r.created_at)}</span>{r.author === 'admin' && <EmailStatus email={r.email} />}</div>
              <p className="whitespace-pre-wrap text-gray-700 mt-1">{r.body}</p>
            </li>
          ))}
        </ul>
      )}
      {!deleted && (
        <div className="mt-3 flex flex-col gap-2">
          {!closed && (
            <div className="flex gap-2 items-start">
              <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2} maxLength={3000} placeholder="Reply in this thread (sent in-app + email)" data-testid={`thread-reply-input-${x.id}`} />
              <Button size="sm" disabled={busy || !reply.trim()} onClick={() => act(() => apiService.admin.replyMemberMessage(member.id, x.id, { body: reply.trim(), force_email: !member.email_notifications }).then((r) => { setReply(''); return r; }), (r) => `Reply sent (${r.data.data.email.status === 'sent' ? 'email sent' : 'email ' + r.data.data.email.status})`)} data-testid={`thread-reply-send-${x.id}`}><Send className="w-4 h-4" /></Button>
            </div>
          )}
          <div>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => act(() => apiService.admin.setMemberThreadStatus(member.id, x.id, closed ? 'open' : 'closed'), () => (closed ? 'Thread reopened' : 'Thread closed — member can no longer reply'))} data-testid={`thread-toggle-status-${x.id}`}>{closed ? 'Reopen thread' : 'Close thread'}</Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default MessagesTab;
